import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Select, Card, Modal, Badge } from "./ui";
import { LANGS, TL } from "../i18n";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Search, Plus, Pencil, Trash2, ChevronDown, ChevronRight, PhoneCall, Settings2, Save, X, Check, Minus, Download, FileText, BarChart3 } from "lucide-react";

const downloadBlob = async (url, filename, onErr) => {
  try {
    const r = await client.get(url, { responseType: "blob" });
    const href = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(href);
  } catch (e) { onErr && onErr(apiErr(e)); }
};

const APPS = [
  { k: "family_tree", l: "Family Tree" },
  { k: "holdings", l: "Holdings" },
  { k: "currency", l: "Currency" },
  { k: "benefits", l: "Benefits" },
];
const TEXT = { first_name: "First name", last_name: "Last name",
  cs_password: "Customer service password",
  phone1: "1st phone", phone2: "2nd phone", email1: "1st email", email2: "2nd email",
  memorable_phrase: "Memorable word or phrase",
  memorable_organization: "Memorable organization (name or acronym)",
  country: "Country", city: "City", address: "Address",
  title: "Title", role: "Role", employer_group: "Employer / user group" };

// Fields the CS client profile requires (apart from the runner's confidential sign-in
// details and marketing opt-ins). Everything NOT listed here (address, title, role,
// employer/group, language, dates, apps…) is optional and shows no required star.
const MANDATORY = new Set(["first_name", "last_name", "phone1", "phone2", "email1", "email2",
  "cs_password", "memorable_phrase", "memorable_organization", "country", "city"]);
const FIELD_MAX = { address: 80 };

const EMPTY = { kind: "primary", preferred_language: "ENG", first_activation_date: "", title: "", role: "",
  first_name: "", last_name: "", cs_password: "", employer_group: "", phone1: "", phone2: "",
  email1: "", email2: "", memorable_phrase: "", memorable_organization: "", country: "", city: "", address: "",
  number_of_applications: 0, application_names: [] };

const SVC_DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-[#DA1E28]", none: "bg-black/20 dark:bg-white/20" };
const ACT = {
  active: { icon: <Check size={14} />, cls: "text-emerald-600", title: "Active" },
  out_of_support: { icon: <Check size={14} />, cls: "text-amber-500", title: "Out of support" },
  deactivated: { icon: <X size={14} />, cls: "text-[#DA1E28]", title: "Deactivated" },
  never: { icon: <Minus size={14} />, cls: "opacity-25", title: "Never licensed" },
};

export default function ClientsSection() {
  const { isAdmin, isSupport } = useApp();
  const isRunner = !isAdmin && !isSupport;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [runnerFilter, setRunnerFilter] = useState("");
  const [runners, setRunners] = useState([]);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [mandatory, setMandatory] = useState(false);   // non-dismissable create
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(() => {
    const params = [];
    if (q) params.push(`q=${encodeURIComponent(q)}`);
    if (runnerFilter) params.push(`runner_id=${runnerFilter}`);
    client.get(`/clients${params.length ? "?" + params.join("&") : ""}`).then((r) => setRows(r.data)).catch((e) => setMsg(apiErr(e)));
  }, [q, runnerFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin || isSupport) client.get("/admin/runners").then((r) => setRunners(r.data)).catch(() => {}); }, [isAdmin, isSupport]);

  useEffect(() => {
    if (!isRunner) return;
    client.get("/clients-mandatory/check").then((r) => {
      if (r.data.needs_creation) {
        setForm({ ...EMPTY, kind: "primary" });
        setEditId(null); setMandatory(true); setOpen(true);
        if (r.data.unpaid_prompt) setMsg("Please also settle the annual service charge from your license cards below if it is not yet paid.");
      }
    }).catch(() => {});
  }, [isRunner]);

  const hasPrimary = rows.some((c) => c.kind === "primary" && (isRunner ? true : c.runner_id));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mandatoryOk = [...MANDATORY].every((k) => String(form[k] || "").trim());
  const toggleApp = (k) => setForm((f) => ({ ...f, application_names: f.application_names.includes(k) ? f.application_names.filter((x) => x !== k) : [...f.application_names, k] }));

  const openCreate = (kind) => {
    setForm({ ...EMPTY, kind });
    if (isAdmin) setForm((f) => ({ ...f, kind, runner_id: runnerFilter || (runners[0]?.id || "") }));
    setEditId(null); setMandatory(false); setOpen(true); setMsg("");
  };
  const openEdit = (c) => { setForm({ ...c }); setEditId(c.id); setMandatory(false); setOpen(true); setMsg(""); };

  const save = async () => {
    try {
      if (isAdmin && !editId && !form.runner_id) { setMsg("Select a runner."); return; }
      if (editId) await client.put(`/clients/${editId}`, form);
      else await client.post("/clients", form);
      setOpen(false); setMandatory(false); load();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete the ${c.kind} client profile? Attached contacts are removed too.`)) return;
    try { await client.delete(`/clients/${c.id}`); load(); } catch (e) { setMsg(apiErr(e)); }
  };

  return (
    <Card className="space-y-4" data-testid="clients-section">
      <div className="flex flex-wrap items-center gap-2">
        <Users size={18} className="text-[var(--primary)]" />
        <span className="font-head text-lg">{TL("Client registration profiles")}</span>
        <Badge color="info">{rows.length}</Badge>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={() => setReportOpen(true)} data-testid="clients-report-open"><BarChart3 size={15} /> {TL("Contact report")}</Button>
          {isAdmin && (
            <Button variant="ghost" onClick={() => setIssueOpen(true)} data-testid="clients-issue-editor-open"><Settings2 size={15} /> {TL("Issue categories")}</Button>
          )}
          {!isSupport && (
            <>
              {(isAdmin || !hasPrimary) && <Button onClick={() => openCreate("primary")} data-testid="client-add-primary"><Plus size={15} /> {TL("New client")}</Button>}
              {(isAdmin || (hasPrimary && !rows.some((c) => c.kind === "alternate" && isRunner))) &&
                <Button variant="subtle" onClick={() => openCreate("alternate")} data-testid="client-add-alternate"><Plus size={15} /> {TL("Alternate client")}</Button>}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={TL("Search any field (name, email, phone, employer, phrase…)")}
            data-testid="clients-search" className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-9 pe-3 text-sm outline-none focus:border-[var(--primary)]" />
        </div>
        {(isAdmin || isSupport) && (
          <Select value={runnerFilter} onChange={(e) => setRunnerFilter(e.target.value)} data-testid="clients-runner-filter" className="w-56">
            <option value="">{TL("All runners")}</option>
            {runners.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
          </Select>
        )}
      </div>

      {msg && <p className="text-sm text-[var(--primary)]" data-testid="clients-msg">{msg}</p>}

      {rows.length === 0 && <p className="text-sm opacity-60" data-testid="clients-empty">{TL("No client profiles yet.")}</p>}

      <div className="space-y-3">
        {rows.map((c) => (
          <ClientCard key={c.id} c={c} expanded={expanded === c.id} onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
            onEdit={() => openEdit(c)} onDelete={() => del(c)} canWrite={!isSupport || true} canDelete={!isSupport} reload={load} />
        ))}
      </div>

      {/* Create / edit modal */}
      <Modal open={open} onClose={() => { if (!mandatory) setOpen(false); }} wide
        title={mandatory ? "Set up your Client Profile (required)" : editId ? `Edit ${form.kind} client` : `New ${form.kind} client`} testid="client-modal">
        <div className="space-y-3">
          {mandatory && <p className="text-sm opacity-75">{TL("Before you continue, please register your primary Client Profile. This is required after your first paid activation.")}</p>}
          {isAdmin && !editId && (
            <Select label={TL("Runner (owner of this profile)")} value={form.runner_id || ""} onChange={(e) => set("runner_id", e.target.value)} data-testid="client-runner-select">
              <option value="">{TL("Select a runner…")}</option>
              {runners.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </Select>
          )}
          {!editId && (
            <Select label={TL("Profile type")} value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="client-kind" disabled={mandatory}>
              <option value="primary">{TL("Primary client")}</option>
              <option value="alternate">{TL("Alternate client")}</option>
            </Select>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label={TL("Preferred language")} value={form.preferred_language} onChange={(e) => set("preferred_language", e.target.value)} data-testid="client-language">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}`}</option>)}
            </Select>
            <Input label={TL("First activation date")} type="date" value={form.first_activation_date || ""} onChange={(e) => set("first_activation_date", e.target.value)} data-testid="client-activation-date" />
            {Object.entries(TEXT).map(([k, l]) => {
              const req = MANDATORY.has(k);
              const mx = FIELD_MAX[k] || 30;
              return (
                <Input key={k} maxLength={mx} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`client-${k}`}
                  label={<>{l}{req && <span className="text-[#DA1E28]" title={TL("Required")}> *</span>} <span className="opacity-50">(max {mx})</span></>} />
              );
            })}
            <Select label={TL("Number of applications")} value={form.number_of_applications} onChange={(e) => set("number_of_applications", parseInt(e.target.value))} data-testid="client-num-apps">
              {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
            </Select>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">{TL("Names of applications")}</div>
            <div className="flex flex-wrap gap-2" data-testid="client-app-names">
              {APPS.map((a) => (
                <label key={a.k} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${form.application_names.includes(a.k) ? "border-[var(--primary)] text-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
                  <input type="checkbox" checked={form.application_names.includes(a.k)} onChange={() => toggleApp(a.k)} data-testid={`client-app-${a.k}`} />
                  {a.l}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <span className="me-auto text-xs opacity-55"><span className="text-[#DA1E28]">*</span> {TL("required")}</span>
            {!mandatory && <Button variant="ghost" onClick={() => setOpen(false)}>{TL("Cancel")}</Button>}
            <Button onClick={save} data-testid="client-save" disabled={!mandatoryOk}><Save size={15} /> {editId ? "Save changes" : "Create profile"}</Button>
          </div>
        </div>
      </Modal>

      {issueOpen && <IssueEditor onClose={() => setIssueOpen(false)} />}
      {reportOpen && <ContactReport runnerFilter={runnerFilter} runners={runners} showRunnerPicker={isAdmin || isSupport} onClose={() => setReportOpen(false)} />}
    </Card>
  );
}

function ClientCard({ c, expanded, onToggle, onEdit, onDelete, canDelete, reload }) {
  const { isSupport, isAdmin } = useApp();
  const svc = c.service_charge_status || { color: "none", flashing: false, label: "" };
  const [contactsOpen, setContactsOpen] = useState(false);
  useEffect(() => {
    if (!(isSupport || isAdmin)) return;
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email1 || c.id.slice(0, 8);
    if (expanded) {
      client.post("/oloso/profile-visit", { action: "open", client_id: c.id, client_name: name }).catch(() => {});
      return () => { client.post("/oloso/profile-visit", { action: "close", client_id: c.id, client_name: name }).catch(() => {}); };
    }
  }, [expanded, isSupport, isAdmin, c.id, c.first_name, c.last_name, c.email1]);
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-4" data-testid={`client-card-${c.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onToggle} className="opacity-60" data-testid={`client-expand-${c.id}`}>{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
        <span className={`inline-block h-3 w-3 rounded-full ${SVC_DOT[svc.color] || SVC_DOT.none} ${svc.flashing && expanded ? "animate-pulse" : ""}`}
          data-testid={`client-svc-dot-${c.id}`} title={svc.label} />
        <span className="font-medium">{c.first_name || "—"} {c.last_name}</span>
        <Badge color={c.kind === "primary" ? "info" : "neutral"}>{c.kind}</Badge>
        <span className="text-xs opacity-60">{c.email1 || c.phone1 || ""}</span>
        <div className="ms-auto flex items-center gap-1" data-testid={`client-activity-${c.id}`}>
          {APPS.map((a) => { const s = (c.activity_status || {})[a.k] || "never"; const cfg = ACT[s]; return (
            <span key={a.k} className={`flex items-center ${cfg.cls}`} title={`${a.l}: ${cfg.title}`} data-testid={`client-act-${a.k}-${c.id}`}>{cfg.icon}</span>
          ); })}
        </div>
        <button onClick={onEdit} className="opacity-70 hover:text-[var(--primary)]" data-testid={`client-edit-${c.id}`}><Pencil size={15} /></button>
        {canDelete && <button onClick={onDelete} className="opacity-70 hover:text-[#DA1E28]" data-testid={`client-del-${c.id}`}><Trash2 size={15} /></button>}
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-black/5 dark:border-white/5 pt-3 text-sm">
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
            <Field l="Title" v={c.title} /><Field l="Role" v={c.role} />
            <Field l="Language" v={c.preferred_language} /><Field l="First activation" v={c.first_activation_date} />
            <Field l="Employer / group" v={c.employer_group} /><Field l="Memorable phrase" v={c.memorable_phrase} />
            <Field l="Memorable organization" v={c.memorable_organization} />
            <Field l="Country" v={c.country} /><Field l="City" v={c.city} />
            <Field l="Address" v={c.address} />
            <Field l="1st phone" v={c.phone1} /><Field l="2nd phone" v={c.phone2} />
            <Field l="1st email" v={c.email1} /><Field l="2nd email" v={c.email2} />
            <Field l="CS password" v={c.cs_password} /><Field l="# applications" v={c.number_of_applications} />
            <Field l="Apps" v={(c.application_names || []).map((a) => APPS.find((x) => x.k === a)?.l).join(", ")} />
            <Field l="Service charge" v={svc.label} />
          </div>
          <Button variant="subtle" onClick={() => setContactsOpen((o) => !o)} data-testid={`client-contacts-toggle-${c.id}`}>
            <PhoneCall size={15} /> Contact log ({c.contact_count || 0})
          </Button>
          {contactsOpen && <ContactLog clientId={c.id} canWrite={!isSupport || true} canCreate={!isSupport} onChange={reload} />}
        </div>
      )}
    </div>
  );
}

const Field = ({ l, v }) => (<div><span className="opacity-55">{l}: </span><span>{v || "—"}</span></div>);

const EMPTY_CONTACT = { contact_date: "", contact_means: "", agent_name_reached: "", issue_group: "", issue_item: "",
  resolutions: [""], escalation_sent: false, escalated_resolutions: [""], escalated_resolver_name: "" };

function ContactLog({ clientId, canCreate, onChange }) {
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => client.get(`/clients/${clientId}/contacts`).then((r) => setList(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); client.get("/issue-categories").then((r) => setGroups(r.data.groups)).catch(() => {}); }, [load]);

  const items = groups.find((g) => g.key === form?.issue_group)?.items || [];
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setArr = (key, i, v) => setForm((f) => { const a = [...f[key]]; a[i] = v; return { ...f, [key]: a }; });
  const addArr = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const rmArr = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, x) => x !== i) }));

  const save = async () => {
    try {
      const payload = { ...form, resolutions: form.resolutions.filter(Boolean), escalated_resolutions: form.escalated_resolutions.filter(Boolean) };
      if (editId) await client.put(`/client-contacts/${editId}`, payload);
      else await client.post(`/clients/${clientId}/contacts`, payload);
      setForm(null); setEditId(null); load(); onChange && onChange();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this contact record?")) return; await client.delete(`/client-contacts/${id}`); load(); onChange && onChange(); };
  const openEdit = (ct) => { setForm({ ...EMPTY_CONTACT, ...ct, resolutions: ct.resolutions?.length ? ct.resolutions : [""], escalated_resolutions: ct.escalated_resolutions?.length ? ct.escalated_resolutions : [""] }); setEditId(ct.id); };

  return (
    <div className="rounded-lg bg-black/[0.03] dark:bg-white/[0.03] p-3" data-testid={`contact-log-${clientId}`}>
      <div className="mb-2 flex items-center justify-end gap-2">
        <button onClick={() => downloadBlob(`/clients/${clientId}/contacts/export?fmt=csv`, `client_contacts_${clientId}.csv`, setMsg)}
          data-testid={`contact-export-csv-${clientId}`} className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Download size={12} /> {TL("CSV")}</button>
        <button onClick={() => downloadBlob(`/clients/${clientId}/contacts/export?fmt=pdf`, `client_contacts_${clientId}.pdf`, setMsg)}
          data-testid={`contact-export-pdf-${clientId}`} className="flex items-center gap-1 rounded-md bg-[var(--primary)] px-2 py-1 text-xs text-white lift"><FileText size={12} /> {TL("PDF")}</button>
      </div>
      {msg && <p className="mb-2 text-xs text-[#DA1E28]">{msg}</p>}
      {list.length === 0 && <p className="text-xs opacity-60">{TL("No contact records yet.")}</p>}
      <div className="space-y-2">
        {list.map((ct) => (
          <div key={ct.id} className="rounded-md border border-black/10 dark:border-white/10 p-2 text-xs" data-testid={`contact-${ct.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{ct.contact_date || "—"}</span>
              <Badge color="neutral">{ct.contact_means || "contact"}</Badge>
              {ct.issue_item && <Badge color="info">{ct.issue_item}</Badge>}
              {ct.escalation_sent && <Badge color="warning">{TL("escalated")}</Badge>}
              <span className="opacity-60">agent: {ct.agent_name_reached || "—"}</span>
              <div className="ms-auto flex gap-1">
                <button onClick={() => openEdit(ct)} data-testid={`contact-edit-${ct.id}`} className="opacity-70 hover:text-[var(--primary)]"><Pencil size={13} /></button>
                {canCreate && <button onClick={() => del(ct.id)} data-testid={`contact-del-${ct.id}`} className="opacity-70 hover:text-[#DA1E28]"><Trash2 size={13} /></button>}
              </div>
            </div>
            {ct.resolutions?.length > 0 && <div className="mt-1 opacity-75">Resolution: {ct.resolutions.join(" · ")}</div>}
            {ct.escalated_resolutions?.length > 0 && <div className="opacity-75">Escalated: {ct.escalated_resolutions.join(" · ")} {ct.escalated_resolver_name ? `(${ct.escalated_resolver_name})` : ""}</div>}
          </div>
        ))}
      </div>

      {canCreate && !form && <Button variant="ghost" className="mt-2" onClick={() => { setForm(EMPTY_CONTACT); setEditId(null); }} data-testid={`contact-add-${clientId}`}><Plus size={14} /> {TL("Add contact")}</Button>}

      {form && (
        <div className="mt-3 space-y-2 rounded-md border border-black/10 dark:border-white/10 p-3" data-testid="contact-form">
          <div className="grid gap-2 sm:grid-cols-3">
            <Input label={TL("Contact date")} type="date" value={form.contact_date || ""} onChange={(e) => setF("contact_date", e.target.value)} data-testid="contact-date" />
            <Input label={TL("Contact means (max 30)")} maxLength={30} value={form.contact_means} onChange={(e) => setF("contact_means", e.target.value)} data-testid="contact-means" />
            <Input label={TL("Agent name reached (max 30)")} maxLength={30} value={form.agent_name_reached} onChange={(e) => setF("agent_name_reached", e.target.value)} data-testid="contact-agent" />
            <Select label={TL("Issue found — category")} value={form.issue_group} onChange={(e) => { setF("issue_group", e.target.value); setF("issue_item", ""); }} data-testid="contact-issue-group">
              <option value="">{TL("Select…")}</option>
              {groups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </Select>
            <Select label={TL("Issue found — item")} value={form.issue_item} onChange={(e) => setF("issue_item", e.target.value)} data-testid="contact-issue-item" disabled={!form.issue_group}>
              <option value="">{TL("Select…")}</option>
              {items.map((it) => <option key={it} value={it}>{it}</option>)}
            </Select>
          </div>
          <ArrayFields label={TL("Resolution made (150 chars each)")} testid="contact-resolution" values={form.resolutions} onChange={(i, v) => setArr("resolutions", i, v)} onAdd={() => addArr("resolutions")} onRemove={(i) => rmArr("resolutions", i)} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.escalation_sent} onChange={(e) => setF("escalation_sent", e.target.checked)} data-testid="contact-escalation-sent" /> {TL("Escalation sent")}</label>
          <ArrayFields label={TL("Escalated resolution (150 chars each)")} testid="contact-escalated" values={form.escalated_resolutions} onChange={(i, v) => setArr("escalated_resolutions", i, v)} onAdd={() => addArr("escalated_resolutions")} onRemove={(i) => rmArr("escalated_resolutions", i)} />
          <Input label={TL("Escalated resolver name (max 30)")} maxLength={30} value={form.escalated_resolver_name} onChange={(e) => setF("escalated_resolver_name", e.target.value)} data-testid="contact-resolver" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setForm(null); setEditId(null); }}>{TL("Cancel")}</Button>
            <Button onClick={save} data-testid="contact-save"><Save size={14} /> {TL("Save contact")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ArrayFields({ label, testid, values, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium opacity-70">{label}</div>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            <textarea maxLength={150} rows={2} value={v} onChange={(e) => onChange(i, e.target.value)} data-testid={`${testid}-${i}`}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-1.5 text-sm outline-none focus:border-[var(--primary)]" />
            {values.length > 1 && <button onClick={() => onRemove(i)} data-testid={`${testid}-rm-${i}`} className="mt-1 opacity-60 hover:text-[#DA1E28]"><Minus size={15} /></button>}
          </div>
        ))}
      </div>
      <button onClick={onAdd} data-testid={`${testid}-add`} className="mt-1 flex items-center gap-1 text-xs text-[var(--primary)]"><Plus size={13} /> {TL("Add field")}</button>
    </div>
  );
}

function ContactReport({ runnerFilter, runners, showRunnerPicker, onClose }) {
  const [rf, setRf] = useState(runnerFilter || "");
  const [period, setPeriod] = useState("all");
  const [rep, setRep] = useState(null);
  const [msg, setMsg] = useState("");
  const qs = `?period=${period}${rf ? `&runner_id=${rf}` : ""}`;
  useEffect(() => { client.get(`/client-contacts-report${qs}`).then((r) => setRep(r.data)).catch((e) => setMsg(apiErr(e))); }, [qs]);
  const stat = [["Clients", rep?.total_clients], ["Contacts", rep?.total_contacts], ["Escalations", rep?.escalations]];
  return (
    <Modal open onClose={onClose} wide title={TL("Contact activity report")} testid="contact-report">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="report-period" className="w-44">
            <option value="all">{TL("All time")}</option>
            <option value="month">{TL("This month")}</option>
            <option value="quarter">{TL("This quarter")}</option>
            <option value="year">{TL("This year")}</option>
          </Select>
          {showRunnerPicker && (
            <Select value={rf} onChange={(e) => setRf(e.target.value)} data-testid="report-runner-filter" className="w-56">
              <option value="">{TL("All runners")}</option>
              {runners.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </Select>
          )}
          {rep?.range_label && <Badge color="info" data-testid="report-range">{rep.range_label}</Badge>}
          <div className="ms-auto flex gap-2">
            <button onClick={() => downloadBlob(`/client-contacts-report/export?fmt=csv&period=${period}${rf ? `&runner_id=${rf}` : ""}`, "contact_activity.csv", setMsg)}
              data-testid="report-export-csv" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><Download size={14} /> {TL("CSV")}</button>
            <button onClick={() => downloadBlob(`/client-contacts-report/export?fmt=pdf&period=${period}${rf ? `&runner_id=${rf}` : ""}`, "contact_activity.pdf", setMsg)}
              data-testid="report-export-pdf" className="flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-white lift"><FileText size={14} /> {TL("PDF")}</button>
          </div>
        </div>
        {msg && <p className="text-sm text-[#DA1E28]">{msg}</p>}
        <div className="grid grid-cols-3 gap-3" data-testid="report-stats">
          {stat.map(([l, v]) => <div key={l} className="rounded-xl glass p-4"><div className="font-head text-2xl">{v ?? "—"}</div><div className="text-xs opacity-60">{l}</div></div>)}
        </div>
        {rep?.trend && (
          <div data-testid="report-trend">
            <div className="mb-1 text-xs font-medium opacity-70">{TL("Contacts — last 12 months")}</div>
            <div className="rounded-xl glass p-3" style={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rep.trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} />
                  <Tooltip formatter={(v) => [v, "contacts"]} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
                  <Bar dataKey="count" fill="var(--primary)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {rep?.by_country && rep.by_country.length > 0 && (
          <div data-testid="report-geo">
            <div className="mb-1 text-xs font-medium opacity-70">{TL("Contacts by country — where support demand concentrates")}</div>
            <div className="rounded-xl glass p-3" style={{ height: Math.max(120, Math.min(10, rep.by_country.length) * 30 + 24) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={rep.by_country.slice(0, 10)} margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="country" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, "contacts"]} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
                  <Bar dataKey="count" fill="var(--primary)" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {rep && (
          <div className="grid gap-4 sm:grid-cols-3 text-sm">
            <ReportList title={TL("By issue category")} rows={rep.by_group} k="group" />
            <ReportList title={TL("By issue item")} rows={rep.by_item} k="item" />
            <ReportList title={TL("By client")} rows={rep.by_client} k="client" />
            <ReportList title={TL("By country")} rows={rep.by_country} k="country" />
            <ReportList title={TL("By city")} rows={rep.by_city} k="city" />
          </div>
        )}
      </div>
    </Modal>
  );
}

const ReportList = ({ title, rows, k }) => (
  <div>
    <div className="mb-1 font-medium opacity-70">{title}</div>
    <div className="space-y-1">
      {(rows || []).length === 0 && <div className="opacity-50">—</div>}
      {(rows || []).map((r, i) => (
        <div key={i} className="flex justify-between border-b border-black/5 dark:border-white/5 py-1">
          <span className="truncate pe-2">{r[k]}</span><span className="font-medium">{r.count}</span>
        </div>
      ))}
    </div>
  </div>
);


function IssueEditor({ onClose }) {
  const [groups, setGroups] = useState([]);
  const [msg, setMsg] = useState("");
  useEffect(() => { client.get("/issue-categories").then((r) => setGroups(r.data.groups)).catch(() => {}); }, []);
  const setItem = (gi, ii, v) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, items: g.items.map((it, j) => j === ii ? v : it) } : g));
  const addItem = (gi) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, items: [...g.items, "New item"] } : g));
  const rmItem = (gi, ii) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g));
  const setLabel = (gi, v) => setGroups((gs) => gs.map((g, i) => i === gi ? { ...g, label: v } : g));
  const addGroup = () => setGroups((gs) => [...gs, { key: `cat_${Date.now()}`, label: "New category", items: ["Item"] }]);
  const rmGroup = (gi) => setGroups((gs) => gs.filter((_, i) => i !== gi));
  const save = async () => {
    try { const { data } = await client.put("/issue-categories", { groups }); setGroups(data.groups); setMsg("Saved."); }
    catch (e) { setMsg(apiErr(e)); }
  };
  return (
    <Modal open onClose={onClose} wide title={TL("Issue-found categories")} testid="issue-editor">
      <div className="space-y-4">
        <p className="text-sm opacity-70">{TL("Edit the vendor-defined categories and items used in the contact log's “Issue found” dropdown.")}</p>
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid={`issue-group-${gi}`}>
            <div className="mb-2 flex items-center gap-2">
              <Input value={g.label} onChange={(e) => setLabel(gi, e.target.value)} data-testid={`issue-group-label-${gi}`} className="max-w-xs" />
              <button onClick={() => rmGroup(gi)} className="ms-auto opacity-60 hover:text-[#DA1E28]" data-testid={`issue-group-rm-${gi}`}><Trash2 size={15} /></button>
            </div>
            <div className="space-y-1">
              {g.items.map((it, ii) => (
                <div key={ii} className="flex items-center gap-2">
                  <Input value={it} onChange={(e) => setItem(gi, ii, e.target.value)} data-testid={`issue-item-${gi}-${ii}`} />
                  <button onClick={() => rmItem(gi, ii)} className="opacity-60 hover:text-[#DA1E28]"><Minus size={15} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => addItem(gi)} className="mt-1 flex items-center gap-1 text-xs text-[var(--primary)]" data-testid={`issue-item-add-${gi}`}><Plus size={13} /> {TL("Add item")}</button>
          </div>
        ))}
        {msg && <p className="text-sm text-[var(--primary)]">{msg}</p>}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={addGroup} data-testid="issue-group-add"><Plus size={15} /> {TL("Add category")}</Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{TL("Close")}</Button>
            <Button onClick={save} data-testid="issue-save"><Save size={15} /> {TL("Save categories")}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
